<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useReducedMotion} from '~/composables/useReducedMotion';
import {TAPPY_ONE_SHOT_MS, TAPPY_PLATE, tappyAnimation} from '~/utils/tappy';

/*
 * Tappy's face (Grant 2026-11-02, the "Tuft" look from the TappyFace design): a chat bubble outlined red on the left
 * and maroon on the right, three tuft hairs, oval eyes with glints and a smile, on a round plate. Pure SVG, so it is
 * crisp from 20px to 200px. One component for every place Tappy shows: the Ask Tappy button, the chat header, the
 * greeting and the small avatar beside each reply.
 *
 * The six moves are the Animations design's keyframes, copied with their timings. The design loops each move so it
 * can be watched; here Hello, Bounce, Peek and Wink play ONCE and settle to idle, while Thinking and Talking loop
 * for as long as the chat keeps them on. Two rules keep the face whole (Grant 2026-11-02, after Hello left Tappy
 * with no hair and no mouth): the resting rules ARE the idle face, and every one-shot keyframe starts from its own
 * hidden pose and ENDS at 100% on the idle face (hair up, smile drawn, scale 1, opacity 1, nothing moved), so no
 * fill-mode or class juggling is needed for the face to be right once the move is over. The loop's exit frames at
 * 90-100% are therefore dropped, and the hair strands' delays are baked into their own keyframes. Idle itself
 * blinks once every 5s. The same geometry and timings drive the app's CustomPainter (tappy_avatar.dart).
 *
 * The bubble outline is drawn as two half paths meeting at x=60 with butt caps: the same pixels as the design's one
 * closed path under two clipPaths, without ids that would collide when several avatars share a page.
 */
const props = defineProps({
  size: {type: [Number, String], default: 40},
  /** idle | hello | bounce | peek | thinking | talking | wink */
  animation: {type: String, default: 'idle'},
  /** brand (red/maroon ink) or white (for a red or maroon background). */
  tone: {type: String, default: 'brand'},
  /** The round plate under the face; off for the top-bar button, which is a tile already. */
  plate: {type: Boolean, default: true},
  /** Whether the idle face blinks now and then; off for the top-bar button, which stays still on every page. */
  blink: {type: Boolean, default: true},
});
const emit = defineEmits(['settled']);

const reducedMotion = useReducedMotion();
const active = ref(tappyAnimation(props.animation, reducedMotion.value));
let timer = null;
const clearTimer = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
};
const play = (animation) => {
  clearTimer();
  active.value = tappyAnimation(animation, reducedMotion.value);
  const ms = TAPPY_ONE_SHOT_MS[active.value];
  // Timers only in a browser: the server renders the first frame and nothing moves there.
  if (ms && typeof window !== 'undefined') {
    timer = setTimeout(() => {
      timer = null;
      active.value = 'idle';
      emit('settled');
    }, ms);
  }
};
watch(() => props.animation, play);
watch(reducedMotion, () => play(props.animation));
// The first move (a greeting's Hello, a header's Peek) needs its settle timer too.
onMounted(() => play(props.animation));
onBeforeUnmount(clearTimer);

const px = computed(() => `${Number(props.size) || 40}px`);
const colours = computed(() => (props.tone === 'white'
  ? {left: '#ffffff', right: 'rgba(255,255,255,0.72)', ink: '#ffffff', glint: '#3a0b15'}
  : {left: '#d32946', right: '#3a0b15', ink: '#3a0b15', glint: '#ffffff'}));
const style = computed(() => ({
  '--tappy-size': px.value,
  '--tappy-plate': TAPPY_PLATE,
  '--tappy-left': colours.value.left,
  '--tappy-right': colours.value.right,
  '--tappy-ink': colours.value.ink,
  '--tappy-glint': colours.value.glint,
}));
</script>

<template>
  <span
      class="tappy"
      :class="[`tappy--${active}`, {'tappy--plate': plate, 'tappy--blink': blink}]"
      :style="style"
      :data-animation="active"
      role="img"
      aria-label="Tappy"
  >
    <svg class="tappy__svg" viewBox="0 0 120 120" focusable="false" aria-hidden="true">
      <g class="av">
        <g class="tl">
          <!-- The bubble: red left half, maroon right half, split at x=60. -->
          <g fill="none" stroke-width="6" stroke-linejoin="round" stroke-linecap="butt">
            <path class="bubble-l" d="M60,20 H38 A24,24 0 0 0 14,44 V68 A24,24 0 0 0 37,91 L34,106 L54,92 H60"/>
            <path class="bubble-r" d="M60,20 H82 A24,24 0 0 1 106,44 V68 A24,24 0 0 1 82,92 H60"/>
          </g>
          <!-- The tuft: one red strand, two maroon. -->
          <g fill="none" stroke-width="4.5" stroke-linecap="round">
            <path class="hr h1 hair-l" d="M53,17 Q50,8 55,3"/>
            <path class="hr h2 hair" d="M60,16 Q60,6 66,2"/>
            <path class="hr h3 hair" d="M67,17 Q69,9 75,7"/>
          </g>
          <g class="ey">
            <g class="lid-l">
              <ellipse class="eye" cx="46" cy="52" rx="6.6" ry="8.2"/>
              <circle class="glint" cx="48.4" cy="49" r="2.6" opacity="0.92"/>
            </g>
            <g class="lid-r">
              <ellipse class="eye" cx="74" cy="52" rx="6.6" ry="8.2"/>
              <circle class="glint" cx="76.4" cy="49" r="2.6" opacity="0.92"/>
            </g>
          </g>
          <path class="sm mouth" d="M47,68 Q60,79 73,68" fill="none" stroke-width="5.5" stroke-linecap="round"/>
          <path class="om mouth-open" d="M48,66 Q60,82 72,66 Z" stroke-width="3" stroke-linejoin="round"/>
          <path class="mini mouth" d="M53,71 Q61,76 70,68" fill="none" stroke-width="5" stroke-linecap="round"/>
          <path class="brow mouth" d="M68,38 Q76,32 83,37" fill="none" stroke-width="3.5" stroke-linecap="round"/>
          <circle class="dt d1 dot-l" cx="90" cy="15" r="3"/>
          <circle class="dt d2 dot-l" cx="101" cy="5" r="4.2"/>
          <circle class="dt d3 dot" cx="115" cy="-7" r="5.6"/>
        </g>
      </g>
    </svg>
  </span>
</template>

<style scoped>
/* Grant 2026-11-02: Tappy's avatar. Geometry and timings from the Animations design; see the script comment. */
.tappy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--tappy-size);
  height: var(--tappy-size);
  vertical-align: middle;
  line-height: 0;
}

.tappy--plate {
  border-radius: 50%;
  background: var(--tappy-plate);
}

/* Peek rises from under the plate's rim, so only then does the plate clip; otherwise the thought dots may drift past it. */
.tappy--peek.tappy--plate {
  overflow: hidden;
}

.tappy__svg {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.tappy--plate .tappy__svg {
  width: 80%;
  height: 80%;
}

.bubble-l,
.hair-l,
.dot-l {
  stroke: var(--tappy-left);
}

.dot-l {
  stroke: none;
  fill: var(--tappy-left);
}

.bubble-r {
  stroke: var(--tappy-right);
}

.hair,
.mouth {
  stroke: var(--tappy-ink);
}

.eye,
.mouth-open,
.dot {
  fill: var(--tappy-ink);
}

.mouth-open {
  stroke: var(--tappy-ink);
}

.glint {
  fill: var(--tappy-glint);
}

/* Pivots in viewBox units (the same numbers the app's painter uses), not fill-box percentages, so a hidden thought
   dot never shifts the head's pivot. */
.tappy svg * {
  transform-box: view-box;
}

.av,
.tl {
  transform-origin: 60px 80px;
}

.h1 { transform-origin: 53px 17px; }
.h2 { transform-origin: 60px 16px; }
.h3 { transform-origin: 67px 17px; }
.ey { transform-origin: 60px 52px; }
.lid-l { transform-origin: 46px 52px; }
.lid-r { transform-origin: 74px 52px; }
.sm { transform-origin: 60px 70.75px; }
.brow { transform-origin: 75.5px 36px; }
.d1 { transform-origin: 90px 15px; }
.d2 { transform-origin: 101px 5px; }
.d3 { transform-origin: 115px -7px; }

.om,
.mini,
.brow,
.dt {
  opacity: 0;
}

/* The resting face: the smile fully drawn (the dash is longer than the path, so offset 0 shows all of it). */
.sm {
  stroke-dasharray: 40;
  stroke-dashoffset: 0;
}

/* 0 Idle: a blink every 5s, nothing else (not on the top-bar button). */
.tappy--idle.tappy--blink .ey { animation: idle-blink 5s ease-in-out infinite; }

@keyframes idle-blink {
  0%, 96% { transform: scaleY(1); }
  98% { transform: scaleY(.1); }
  100% { transform: scaleY(1); }
}

/* 1 Hello: pops in, the hair springs up (each strand .08s after the last), the smile draws on, a tilt-wave, two
   blinks. Once. */
.tappy--hello .av { animation: h-pop 6s cubic-bezier(.34, 1.56, .64, 1) 1; }
.tappy--hello .h1 { animation: h-hair-1 6s ease-out 1; }
.tappy--hello .h2 { animation: h-hair-2 6s ease-out 1; }
.tappy--hello .h3 { animation: h-hair-3 6s ease-out 1; }
.tappy--hello .sm { animation: h-smile 6s ease-out 1; }
.tappy--hello .tl { animation: h-tilt 6s ease-in-out 1; }
.tappy--hello .ey { animation: blink 6s ease-in-out 1; }

@keyframes h-pop {
  0% { transform: scale(.4); opacity: 0; }
  8% { transform: scale(1.08); opacity: 1; }
  13%, 100% { transform: scale(1); opacity: 1; }
}

@keyframes h-hair-1 {
  0%, 10% { transform: scaleY(0); }
  17% { transform: scaleY(1.15); }
  21%, 100% { transform: scaleY(1); }
}

@keyframes h-hair-2 {
  0%, 11.33% { transform: scaleY(0); }
  18.33% { transform: scaleY(1.15); }
  22.33%, 100% { transform: scaleY(1); }
}

@keyframes h-hair-3 {
  0%, 12.67% { transform: scaleY(0); }
  19.67% { transform: scaleY(1.15); }
  23.67%, 100% { transform: scaleY(1); }
}

@keyframes h-smile {
  0%, 15% { stroke-dashoffset: 40; }
  25%, 100% { stroke-dashoffset: 0; }
}

@keyframes h-tilt {
  0%, 24% { transform: rotate(0); }
  29% { transform: rotate(-7deg); }
  34% { transform: rotate(6deg); }
  39% { transform: rotate(-3deg); }
  43%, 100% { transform: rotate(0); }
}

@keyframes blink {
  0%, 36% { transform: scaleY(1); }
  38% { transform: scaleY(.1); }
  40%, 74% { transform: scaleY(1); }
  76% { transform: scaleY(.1); }
  78%, 100% { transform: scaleY(1); }
}

/* 2 Bounce: two happy hops with a squash, the hair wiggling. Once. */
.tappy--bounce .av { animation: b-bounce 2.4s ease-in-out 1; }
.tappy--bounce .hr { animation: b-wiggle 2.4s ease-in-out 1; }
.tappy--bounce .h2 { animation-delay: .06s; }
.tappy--bounce .h3 { animation-delay: .12s; }
.tappy--bounce .ey { animation: blink 4.8s ease-in-out 1; }

@keyframes b-bounce {
  0%, 100% { transform: translateY(0) scale(1, 1); }
  12% { transform: translateY(0) scale(1.06, .92); }
  28% { transform: translateY(-12px) scale(.96, 1.05); }
  44% { transform: translateY(0) scale(1.05, .94); }
  56% { transform: translateY(-6px) scale(.98, 1.02); }
  68% { transform: translateY(0) scale(1, 1); }
}

@keyframes b-wiggle {
  0%, 100% { transform: rotate(0); }
  28% { transform: rotate(-14deg); }
  44% { transform: rotate(10deg); }
  56% { transform: rotate(-6deg); }
  68% { transform: rotate(0); }
}

/* 3 Peek: rises into view, looks left and right, then smiles. Once. */
.tappy--peek .av { animation: p-rise 5s cubic-bezier(.22, 1, .36, 1) 1; }
.tappy--peek .ey { animation: p-look 5s ease-in-out 1; }
.tappy--peek .sm { animation: p-smile 5s ease-out 1; }

@keyframes p-rise {
  0% { transform: translateY(70px); }
  18%, 100% { transform: translateY(0); }
}

@keyframes p-look {
  0%, 22% { transform: translateX(0); }
  30%, 40% { transform: translateX(-4px); }
  48%, 58% { transform: translateX(4px); }
  66%, 100% { transform: translateX(0); }
}

@keyframes p-smile {
  0%, 62% { transform: scaleX(.6); }
  72%, 100% { transform: scaleX(1); }
}

/* 4 Thinking: a curious half-smile and a raised brow, eyes up-right at a trail of thought dots. Loops while on. */
.tappy--thinking .ey { animation: t-eyes 3s ease-in-out infinite; }
.tappy--thinking .sm { opacity: 0; }
.tappy--thinking .mini { opacity: 1; }
.tappy--thinking .brow { opacity: 1; animation: t-brow 3s ease-in-out infinite; }
.tappy--thinking .hr { animation: t-sway 3s ease-in-out infinite; }
.tappy--thinking .h2 { animation-delay: .15s; }
.tappy--thinking .h3 { animation-delay: .3s; }
.tappy--thinking .dt { animation: t-dot 1.5s ease-in-out infinite; }
.tappy--thinking .d2 { animation-delay: .25s; }
.tappy--thinking .d3 { animation-delay: .5s; }
.tappy--thinking .tl { animation: t-tilt 3s ease-in-out infinite; }

@keyframes t-brow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
}

@keyframes t-eyes {
  0%, 100% { transform: translate(3.5px, -4px); }
  45% { transform: translate(3.5px, -4px); }
  50% { transform: translate(3.5px, -4px) scaleY(.15); }
  55% { transform: translate(3.5px, -4px); }
}

@keyframes t-sway {
  0%, 100% { transform: rotate(-6deg); }
  50% { transform: rotate(6deg); }
}

@keyframes t-dot {
  0%, 100% { opacity: .35; transform: scale(.8); }
  40% { opacity: 1; transform: scale(1.15); }
}

@keyframes t-tilt {
  0%, 100% { transform: rotate(4deg); }
  50% { transform: rotate(2deg); }
}

/* 5 Talking: the mouth alternates smile and open with a small nod. Loops while on. */
.tappy--talking .sm { animation: k-smile 1.8s steps(1) infinite; }
.tappy--talking .om { animation: k-open 1.8s steps(1) infinite; }
.tappy--talking .ey { animation: blink 5.4s ease-in-out infinite; }
.tappy--talking .av { animation: k-nod 1.8s ease-in-out infinite; }

@keyframes k-smile {
  0%, 15%, 30%, 45%, 70%, 100% { opacity: 1; }
  8%, 22%, 38%, 55% { opacity: 0; }
}

@keyframes k-open {
  0%, 15%, 30%, 45%, 70%, 100% { opacity: 0; }
  8%, 22%, 38%, 55% { opacity: 1; }
}

@keyframes k-nod {
  0%, 100% { transform: translateY(0); }
  25% { transform: translateY(-2px); }
  50% { transform: translateY(1px); }
}

/* 6 Wink: a tilt, a wink and a bigger grin. Once. */
.tappy--wink .lid-l { animation: w-wink 3.6s ease-in-out 1; }
.tappy--wink .sm { animation: w-grin 3.6s ease-in-out 1; }
.tappy--wink .tl { animation: w-tilt 3.6s ease-in-out 1; }

@keyframes w-wink {
  0%, 30% { transform: scaleY(1); }
  36%, 58% { transform: scaleY(.1); }
  64%, 100% { transform: scaleY(1); }
}

@keyframes w-grin {
  0%, 30% { transform: scaleX(1); }
  36%, 58% { transform: scaleX(1.25); }
  64%, 100% { transform: scaleX(1); }
}

@keyframes w-tilt {
  0%, 30% { transform: rotate(0); }
  36%, 58% { transform: rotate(5deg); }
  64%, 100% { transform: rotate(0); }
}

/* Reduced motion: the resting face, whatever the chat asked for (the script also picks idle, this covers the first
   paint and the idle blink). The resting rules are the idle face, so stopping the animations is all it takes. */
@media (prefers-reduced-motion: reduce) {
  .tappy svg * {
    animation: none !important;
  }

  .tappy .sm {
    opacity: 1;
  }

  .tappy .om,
  .tappy .mini,
  .tappy .brow,
  .tappy .dt {
    opacity: 0;
  }
}
</style>
